import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { DataTable, getFlatRowSelection, getInitialRowSelectionState } from "@msuite/ui/table";
import { type SelectArticlesSchema, selectArticlesSchema } from "@msuite/utils/schemas";
import type { ArticleListItem } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { articlesTableColumns, withoutStockThresholds } from "@/components/stock";
import { useArticles } from "@/hooks/use-articles";

// Reached from the order form's articles tab, so it drops the restocking thresholds for the same
// reason that tab does: whoever is choosing what to order is not the one keeping the shelves full.
const tableColumns = withoutStockThresholds(articlesTableColumns);

/** Props Interface */
interface SelectArticlesModalProps extends ModalProps {
	onComplete: (articleIds: string[]) => Promise<void>;
	initialArticleIds: string[];
	enableRowSelection?: (item: ArticleListItem) => boolean;
}

export function SelectArticlesModal({
	onClose,
	isOpen,
	onComplete,
	initialArticleIds,
	enableRowSelection,
}: SelectArticlesModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm({
		resolver: resolver(selectArticlesSchema),
	});
	const { articles, isLoading, refetch, queryKey } = useArticles();

	/** Functions */
	async function handleOnSubmit(json: SelectArticlesSchema) {
		await onComplete(json.articleIds);
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.select_articles")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				width="90vw"
			>
				<Modal.Body>
					<Modal.TableWrapper>
						<DataTable
							data={articles}
							columns={tableColumns}
							queryKey={queryKey}
							isLoading={isLoading}
							refetch={refetch}
							// Its own key rather than the articles screen's: the two tables no longer
							// carry the same columns, and a shared key would restore a layout naming
							// columns this one does not have — including the pinned thresholds it
							// deliberately drops.
							persistenceKey="select-articles-table"
							initialPinningState={{
								start: [],
								end: ["availableQuantity"],
							}}
							onRowSelectionChange={(state) => {
								const articleIds = getFlatRowSelection(state);
								methods.setValue("articleIds", articleIds);
							}}
							initialRowSelectionState={getInitialRowSelectionState(initialArticleIds)}
							enableRowSelection={(item) => {
								const isPreSelected = !initialArticleIds.includes(item.id);
								if (enableRowSelection) {
									return isPreSelected && enableRowSelection(item.original as ArticleListItem);
								} else {
									return isPreSelected;
								}
							}}
							enableMultiRowSelection
							menu={null}
						/>
					</Modal.TableWrapper>
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
